import { Box, Heading, Tag, Text, Button } from '@everlywell/ui-kit';
import { Link as GatsbyLink } from 'gatsby';
import React from 'react';
import { Metadata } from 'utils/types/contentfulTypes';

import BulletPoints, { BulletPointsProps } from '../BulletPoints';
import PricingSelectorV2Accordions, {
  PricingSelectorV2AccordionsProps,
} from '../PricingSelectorV2Accordions';

// Types ----------

type CTA = {
  displayName?: string;
  label?: string;
  metadata?: Metadata;
  url?: string;
};

type Badge = {
  text: string;
  color: string;
  backgroundColor?: string | null;
};

export type SecondaryCardProps = {
  title: string;
  description?: string;
  cta?: CTA;
};

export type PriceDetail = {
  title: string;
  price: string;
  badge?: Badge | null;
  oldPrice?: string | null;
};

export interface CardProps {
  priceDetail: PriceDetail;
  bulletPoints: BulletPointsProps;
  accordions?: PricingSelectorV2AccordionsProps;
  cta: CTA;
  isExpanded: boolean;
  secondaryCard?: SecondaryCardProps;
  handleCollapsedCTAClick: () => void;
  onCtaClick: (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    ctaUrl: string,
    ctaLabel: string,
  ) => void;
}

// Card ------

const Card: React.FC<CardProps> = (props) => {
  const {
    priceDetail,
    bulletPoints,
    accordions,
    cta,
    secondaryCard,
    isExpanded,
    handleCollapsedCTAClick,
    onCtaClick,
  } = props;

  const { badge, title, price, oldPrice } = priceDetail;

  const formattedPrice = price.split('.');
  const formattedOldPrice = oldPrice?.split('.');

  return (
    <Box
      backgroundColor="white"
      borderRadius="24px"
      height="100%"
      padding="8"
      width="100%"
    >
      {/* Expanded Card */}
      <Box display={isExpanded ? 'block' : 'none'}>
        <Box
          borderBottom="1px solid"
          borderColor="tints.darkCream"
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          marginBottom="8"
          paddingBottom="8"
          width="100%"
        >
          {/* Badge */}
          {badge ? (
            <Box
              display="flex"
              justifyContent={['start', null, 'end']}
              marginBottom={['4', null, 0]}
              width="100%"
            >
              <Tag height="28px" variant="callout" size="sm">
                {badge.text}
              </Tag>
            </Box>
          ) : null}

          {/* Title and Pricing */}
          <Box
            display="flex"
            flexWrap={['wrap', null, 'nowrap']}
            justifyContent="space-between"
            width="100%"
          >
            <Box width={['100%', null, '50%']} marginBottom={['2', null, 0]}>
              <Heading size="md">{title}</Heading>
            </Box>

            <Box
              alignItems="end"
              display="flex"
              justifyContent={['flex-start', null, 'flex-end']}
              width={['100%', null, '50%']}
            >
              {formattedOldPrice ? (
                <Box
                  alignItems="flex-end"
                  display="flex"
                  flexWrap="nowrap"
                  marginLeft={['10px', null, 0]}
                  marginRight="3"
                  order={[2, null, 1]}
                  position="relative"
                >
                  <Box
                    backgroundColor="utility.sale"
                    height="2"
                    left="0"
                    position="absolute"
                    top="50%"
                    transform="translate(0, -50%)"
                    width="100%"
                  />
                  <Heading size="sm" color="tints.uiGrey">
                    $
                  </Heading>
                  <Heading size="md" color="tints.uiGrey">
                    {formattedOldPrice[0]}
                  </Heading>
                  <Heading size="sm" color="tints.uiGrey">
                    .{formattedOldPrice[1]}
                  </Heading>
                </Box>
              ) : null}

              <Box
                alignItems="flex-end"
                display="flex"
                flexWrap="nowrap"
                order={[1, null, 2]}
              >
                <Heading size="md" color="tints.black">
                  $
                </Heading>
                <Heading size="2xl" color="tints.black">
                  {formattedPrice[0]}
                </Heading>
                {formattedPrice.length > 1 ? (
                  <Heading size="md" color="tints.black">
                    .{formattedPrice[1]}
                  </Heading>
                ) : null}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Bullet list */}
        {bulletPoints ? (
          <Box marginBottom="4">
            <BulletPoints {...bulletPoints} />
          </Box>
        ) : null}

        {/* Accordions */}
        {accordions ? (
          <Box data-testid="accordions">
            <PricingSelectorV2Accordions accordions={accordions.accordions} />
          </Box>
        ) : null}

        {/* CTA */}
        <Box display="flex" justifyContent="flex-end" marginTop="8">
          <Button
            as={GatsbyLink}
            size="md"
            to={cta.url || ''}
            variant="primary"
            width="100%"
            onClick={(event) =>
              onCtaClick(event, cta.url || '', cta.label || '')
            }
          >
            {cta.label}
          </Button>
        </Box>
      </Box>

      {/* Collapsed Card */}
      {secondaryCard && (
        <Box
          display={isExpanded ? 'none' : 'flex'}
          alignContent="space-between"
          flexWrap="wrap"
          height="100%"
        >
          <Heading width="80%">{secondaryCard.title}</Heading>

          <Box alignSelf="flex-end">
            <Text marginBottom={6}>{secondaryCard.description}</Text>
            <Button
              size="md"
              onClick={() => handleCollapsedCTAClick()}
              isDisabled={false}
              variant="secondary"
              width="100%"
            >
              {secondaryCard.cta?.label}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// Export ---------

export default React.memo(Card);
